/**
 * SendInvoiceStep
 *
 * The final "Send Invoice" step in the InvoiceGenerationWizard.
 * Also used as a standalone dialog when "Send" is clicked from the
 * Draft invoice ... menu in the billing tab.
 *
 * TODO (Engineering): Wire up "Send Now" to POST /api/invoices/{id}/send
 * with the email payload. Replace mock template data with GET /api/email-templates.
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { EmailAddressInput } from '@/components/billing/EmailAddressInput';
import { Badge } from '@/components/ui/badge';
// Card/CardContent removed — no longer used after replacing holding page with MFE
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import {
  Send,
  ArrowLeft,
  Save,
  X,
  Eye,
  Pencil,
  Paperclip,
  FileText,
  Plus,
  Trash2,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { DEFAULT_EMAIL_TEMPLATES } from './EmailTemplateEditor';
import type { EmailTemplate, EmailTemplateType } from './EmailTemplateEditor';
import { fetchEmailTemplates } from '@/services/emailTemplateService';
import type { ApiEmailTemplate } from '@/services/emailTemplateService';
import { useDataMode } from '@/contexts/DataModeContext';
import { useAuth } from '@/providers/AuthProvider';
import { openMfe, closeMfe } from '@access-evo/evo-api';
import {
  getStoredInvoicePdf,
  blobToBase64,
  generateInvoicePdf,
  fetchFullCompanyDetails,
  fetchDefaultInvoiceTemplate,
  buildPreviewDataForPdf,
} from '@/services/invoice-pdf-service';
import type { InvoicePdfTransaction } from '@/types/invoice-pdf';
import { fetchBillingInvoiceById } from '@/lib/data-service';
import { useCompany } from '@/contexts/CompanyContext';
import {
  SENDIT_MFE_NAME,
  isSendItCallbackDetail,
  type SendItCallbackDetail,
  type SendItEmailPayload,
  type SendItMfeOptions,
} from '@/contracts/sendit-mfe';
import { logMfeFailure } from '@/services/mfe-failure-logger';
import { cn } from '@/lib/utils';

// ─────────────────────────────────────────────────────────────────────────────
// Merge field resolution
// ─────────────────────────────────────────────────────────────────────────────

/** Replace {dot.notation} tokens with real values in a plain-text string.
 *  Handles both braced ("{client.email}") and bare ("client.email") token
 *  formats so templates stored via the API still resolve correctly. */
function resolveTokens(text: string, data: Record<string, string>): string {
  return Object.entries(data).reduce(
    (acc, [token, value]) => {
      const after = acc.replaceAll(token, value);
      if (after === acc && token.startsWith('{') && token.endsWith('}')) {
        return acc.replaceAll(token.slice(1, -1), value);
      }
      return after;
    },
    text,
  );
}

/** Resolve template recipient addresses, dropping empty entries. */
function resolveAddresses(addresses: string[] | undefined, data: Record<string, string>): string[] {
  return addresses?.map((a) => resolveTokens(a, data)).filter(Boolean) ?? [];
}

/**
 * Convert a template's bodyHtml (which contains merge-chip <span> elements)
 * into resolved plain text suitable for the send form body field.
 *
 * Steps:
 *  1. Replace chip spans  →  resolved value from tokenData (or the raw token
 *     as a fallback so nothing is silently swallowed)
 *  2. Convert <br> / </p> block endings → newlines
 *  3. Strip all remaining HTML tags
 *  4. Decode basic HTML entities (&amp; &lt; &gt; &nbsp;)
 *  5. Collapse triple+ newlines to double (paragraph spacing)
 */
function resolveHtmlToPlainText(html: string, data: Record<string, string>): string {
  if (!html) return '';

  let text = html;

  // 1. Replace chip spans with resolved values
  text = text.replace(
    /<span[^>]*data-merge-token="([^"]+)"[^>]*>[^<]*<\/span>/g,
    (_, token: string) => data[token] ?? token,
  );

  // 2. Block element endings → newlines
  text = text.replace(/<\/p>/gi, '\n');
  text = text.replace(/<br\s*\/?>/gi, '\n');

  // 3. Strip remaining tags
  text = text.replace(/<[^>]+>/g, '');

  // 4. Decode HTML entities
  text = text
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ')
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"');

  // 5. Normalise whitespace — collapse 3+ newlines to 2
  text = text.replace(/\n{3,}/g, '\n\n').trim();

  return text;
}

/**
 * Normalise body HTML so blank lines survive dangerouslySetInnerHTML rendering.
 *
 * 1. Empty `<p></p>` tags (Tiptap's representation of blank lines) collapse in
 *    the browser because they have zero content height and adjacent margins
 *    collapse.  Injecting a `<br>` gives them height.
 * 2. If the content has no block-level HTML at all (plain text from the API
 *    with `\n` characters), convert newlines to `<br>` tags.
 */
function normaliseHtmlForPreview(html: string): string {
  let out = html;
  // Empty paragraphs → visible blank lines
  out = out.replace(/<p>\s*<\/p>/gi, '<p><br></p>');
  // Plain text (no block elements) — convert newlines to <br>
  if (!/<\/(?:p|div|br|h[1-6]|ul|ol|li|table|blockquote)\b/i.test(out)) {
    out = out.replace(/\r\n/g, '\n').replace(/\n/g, '<br>');
  }
  return out;
}

/**
 * Render an edited (plain-text) body as HTML for the preview pane: HTML special
 * characters are escaped and newlines become `<br>` so the preview matches what
 * the recipient will see.
 */
function plainTextToPreviewHtml(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return escaped.replace(/\r\n/g, '\n').replace(/\n/g, '<br>');
}

/**
 * Resolve chip spans in bodyHtml and replace tokens, returning HTML suitable
 * for dangerouslySetInnerHTML in the preview pane (no chip spans, real values).
 */
function resolveHtmlForSendPreview(html: string, data: Record<string, string>): string {
  if (!html) return '';

  // Replace chip spans with plain resolved text
  let resolved = html.replace(
    /<span[^>]*data-merge-token="([^"]+)"[^>]*>[^<]*<\/span>/g,
    (_, token: string) => {
      const value = data[token];
      // If resolved, wrap in a subtle highlight so the PM can see what was filled in
      return value
        ? `<span class="resolved-token">${value}</span>`
        : `<span class="unresolved-token">${token}</span>`;
    },
  );

  // Also resolve any remaining raw tokens in subject / addresses that sneak through
  resolved = resolveTokens(resolved, data);

  // Ensure blank lines render correctly
  resolved = normaliseHtmlForPreview(resolved);

  return resolved;
}

// ─────────────────────────────────────────────────────────────────────────────
// Attachment types & helpers
// ─────────────────────────────────────────────────────────────────────────────

interface Attachment {
  /** Unique id (auto-generated) */
  id: string;
  /** Display name shown in the list */
  name: string;
  /** Human-readable file size, e.g. "245 KB" */
  size: string;
  /** Whether this was auto-attached (i.e. the invoice PDF) vs manually added */
  isAutoAttached: boolean;
  /** Underlying File object for manually added files; null for auto-attachments */
  file: File | null;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────

export interface SendInvoiceStepProps {
  /** Invoice number being sent, e.g. "INV-2024-042" */
  invoiceNumber: string;
  /** Numeric invoice ID from the backend — used to fetch stored PDF and line items */
  invoiceId?: number | string;
  /** Client billing email address */
  clientEmail?: string;
  /** Client contact name */
  contactName?: string;
  /** Client contact first name (from Finance System contactDetails.firstName) */
  contactFirstName?: string;
  /** Client company name */
  clientName?: string;
  /** Client phone number */
  clientPhone?: string;
  /** Client account number / code */
  clientAccountNumber?: string;
  /** Client payment terms in days */
  clientPaymentTermsDays?: string;
  /** Project name */
  projectName?: string;
  /** Project code */
  projectCode?: string;
  /** Invoice total formatted, e.g. "£12,450.00" */
  invoiceTotal?: string;
  /** Invoice date formatted */
  invoiceDate?: string;
  /** Due date formatted */
  dueDate?: string;
  /** Company name */
  companyName?: string;
  /** Sender name */
  senderName?: string;
  /** Sender email (reply-to) */
  senderEmail?: string;
  /** Called when "Save as Draft" is clicked — receives the current email form data */
  onSaveAsDraft?: (emailData: { to: string[]; cc: string[]; bcc: string[]; subject: string; body: string; templateId: string }) => void;
  /** Pre-saved email draft data to restore when re-opening a draft */
  initialEmailDraft?: { to?: string; cc?: string; bcc?: string; subject?: string; body?: string; templateId?: string };
  /** Called when "Send Now" is confirmed (after platform emailer screen) */
  onSent?: () => void;
  /** Called when "Back" is clicked (wizard mode only) */
  onBack?: () => void;
  /** Called when "Cancel" is clicked */
  onCancel?: () => void;
  /** When true renders as a standalone Dialog instead of an embedded step */
  asDialog?: boolean;
  /** Controls dialog open state when asDialog=true */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * ID of the email template pre-assigned to this project for invoices.
   * When provided, the template selector will default to this template instead
   * of the global default.
   */
  defaultEmailTemplateId?: string | number | null;
  /**
   * Base64-encoded invoice PDF to attach to the email sent via the SendIt MFE.
   * When provided, the PDF is included in the bulkPayload attachments so the
   * Evo Emailer receives the file.
   */
  invoicePdfBase64?: string;
  /**
   * Async callback invoked before the emailer MFE is opened.
   * Used to create the sales transaction and generate/store the PDF.
   * The emailer MFE is only opened when this returns { success: true }.
   */
  onBeforeSend?: () => Promise<{ success: boolean; pdfBase64?: string; invoiceNumber?: string }>;
  /**
   * Called when the Emailer MFE modal is dismissed (Cancel, X, or Close).
   * The parent should use this to close any enclosing wizard/dialog.
   */
  onEmailerClosed?: () => void;
}

/** Shape of email data passed back to the parent via onSaveAsDraft */
export interface DraftEmailData {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  body: string;
  templateId: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Inner form component
// ─────────────────────────────────────────────────────────────────────────────

function mapApiToFrontend(api: ApiEmailTemplate): EmailTemplate {
  let parsedVars: { to?: string[]; cc?: string[]; bcc?: string[] } = {};
  if (api.variables) {
    try { parsedVars = JSON.parse(api.variables); } catch { /* ignore */ }
  }
  return {
    id: String(api.id),
    name: api.name,
    type: (api.category || 'invoice') as EmailTemplateType,
    to: parsedVars.to ?? [],
    cc: parsedVars.cc ?? [],
    bcc: parsedVars.bcc ?? [],
    subject: api.subject,
    body: '',
    bodyHtml: api.body,
    isDefault: api.isDefault,
    createdAt: new Date(api.createdAt),
    updatedAt: new Date(api.updatedAt),
  };
}

function SendForm({
  invoiceNumber,
  invoiceId,
  clientEmail,
  contactName,
  contactFirstName,
  clientName,
  clientPhone,
  clientAccountNumber,
  clientPaymentTermsDays,
  projectName,
  projectCode,
  invoiceTotal,
  invoiceDate,
  dueDate,
  companyName,
  senderName,
  senderEmail,
  onSaveAsDraft,
  onSent,
  onBack,
  onCancel,
  defaultEmailTemplateId,
  invoicePdfBase64,
  initialEmailDraft,
  onBeforeSend,
  onEmailerClosed,
  onMfeOpenChange,
}: SendInvoiceStepProps & { onMfeOpenChange?: (open: boolean) => void }) {
  const { isDemoMode } = useDataMode();
  const { instanceId } = useAuth();
  const { currentCompany } = useCompany();
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>(
    DEFAULT_EMAIL_TEMPLATES.filter((t) => t.type === 'invoice'),
  );

  // ── PDF attachment — resolve from prop or fetch stored PDF ─────────────
  const [resolvedPdfBase64, setResolvedPdfBase64] = useState<string | undefined>(invoicePdfBase64);

  useEffect(() => {
    if (invoicePdfBase64) {
      setResolvedPdfBase64(invoicePdfBase64);
      return;
    }
    // Use numeric invoiceId (consistent with rest of codebase), fall back to invoiceNumber
    const pdfKey = invoiceId != null ? String(invoiceId) : invoiceNumber;
    if (!pdfKey) return;
    let cancelled = false;
    (async () => {
      try {
        const stored = await getStoredInvoicePdf(pdfKey);
        if (!cancelled && stored) {
          const b64 = await blobToBase64(stored.pdfBlob);
          if (!cancelled) setResolvedPdfBase64(b64);
          return;
        }
      } catch { /* fetch failed */ }
      // No stored PDF — generate on-the-fly (e.g. draft invoices)
      if (cancelled || invoiceId == null) return;
      try {
        const { invoice: inv, lineItems } = await fetchBillingInvoiceById(Number(invoiceId));
        if (cancelled) return;
        const transaction: InvoicePdfTransaction = {
          invoiceNumber: inv.invoiceNumber ?? invoiceNumber,
          date: inv.invoiceDate ?? new Date().toISOString(),
          dueDate: inv.dueDate ?? undefined,
          status: 'Draft',
          description: inv.description ?? '',
          amount: inv.grossTotal,
          projectName,
          accountName: clientName,
        };
        const compDetails = await fetchFullCompanyDetails(currentCompany);
        const template = await fetchDefaultInvoiceTemplate(defaultEmailTemplateId);
        const previewData = template && compDetails
          ? buildPreviewDataForPdf(transaction, compDetails, lineItems)
          : undefined;
        const result = await generateInvoicePdf(transaction, compDetails, lineItems, template, previewData, inv.notes || undefined);
        if (cancelled) return;
        const b64 = await blobToBase64(result.pdfBlob);
        if (!cancelled) setResolvedPdfBase64(b64);
      } catch { /* generation failed — send without attachment */ }
    })();
    return () => { cancelled = true; };
  }, [invoiceNumber, invoiceId, invoicePdfBase64, currentCompany, projectName, clientName, defaultEmailTemplateId]);

  useEffect(() => {
    if (isDemoMode) {
      setEmailTemplates(DEFAULT_EMAIL_TEMPLATES.filter((t) => t.type === 'invoice'));
      return;
    }
    let cancelled = false;
    fetchEmailTemplates()
      .then((apiTemplates) => {
        if (!cancelled) {
          setEmailTemplates(apiTemplates.map(mapApiToFrontend).filter((t) => t.type === 'invoice'));
        }
      })
      .catch((err) => {
        console.error('Failed to load email templates:', err);
        if (!cancelled) {
          setEmailTemplates(DEFAULT_EMAIL_TEMPLATES.filter((t) => t.type === 'invoice'));
        }
      });
    return () => { cancelled = true; };
  }, [isDemoMode]);

  /**
   * Latch that is true while a saved draft's email data is in force. While set,
   * asynchronous template (re)loads must NOT overwrite the restored To/CC/Subject/
   * Body/template. It is cleared only when the user explicitly picks a different
   * template from the selector (see handleTemplateChange).
   */
  const draftLatchRef = useRef(
    !!(initialEmailDraft && (
      initialEmailDraft.to || initialEmailDraft.cc || initialEmailDraft.bcc ||
      initialEmailDraft.subject || initialEmailDraft.body || initialEmailDraft.templateId
    )),
  );

  const [selectedTemplateId, setSelectedTemplateId] = useState<string>(() => {
    // When re-opening a saved draft, honour the template that was selected then.
    if (initialEmailDraft?.templateId) {
      const saved = emailTemplates.find((t) => t.id.toString() === initialEmailDraft.templateId!.toString());
      if (saved) return saved.id.toString();
    }
    // Use the project-level default email template if one has been assigned,
    // otherwise fall back to the global default invoice template.
    if (defaultEmailTemplateId != null) {
      const match = emailTemplates.find((t) => t.id.toString() === defaultEmailTemplateId.toString());
      if (match) return match.id.toString();
    }
    return (
      emailTemplates.find((t) => t.type === 'invoice' && t.isDefault)?.id ??
      emailTemplates[0]?.id ??
      ''
    );
  });

  // Re-select the default template when the template list changes (e.g. after
  // the API response replaces mock data with real templates whose IDs differ).
  useEffect(() => {
    // A saved draft's template takes precedence until the user picks another one.
    if (draftLatchRef.current && initialEmailDraft?.templateId) {
      const saved = emailTemplates.find((t) => t.id.toString() === initialEmailDraft.templateId!.toString());
      if (saved) {
        setSelectedTemplateId(saved.id.toString());
        return;
      }
    }
    if (defaultEmailTemplateId != null) {
      const match = emailTemplates.find((t) => t.id.toString() === defaultEmailTemplateId.toString());
      if (match) {
        setSelectedTemplateId(match.id.toString());
        return;
      }
    }
    const best =
      emailTemplates.find((t) => t.type === 'invoice' && t.isDefault) ??
      emailTemplates[0];
    if (best) setSelectedTemplateId(best.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailTemplates, defaultEmailTemplateId]);

  const splitAddresses = (raw?: string): string[] =>
    raw ? raw.split(',').map((s) => s.trim()).filter(Boolean) : [];

  const [to, setTo]   = useState<string[]>(
    initialEmailDraft?.to ? splitAddresses(initialEmailDraft.to) : (clientEmail ? [clientEmail] : []),
  );
  const [cc, setCc]   = useState<string[]>(splitAddresses(initialEmailDraft?.cc));
  const [bcc, setBcc] = useState<string[]>(splitAddresses(initialEmailDraft?.bcc));
  const [subject, setSubject] = useState(initialEmailDraft?.subject ?? '');
  const [body, setBody] = useState(initialEmailDraft?.body ?? '');
  /** Resolved HTML for the preview pane — updated whenever the template changes */
  const [previewHtml, setPreviewHtml] = useState('');
  /** Tracks whether the user has manually edited the body in the Edit tab */
  const [bodyManuallyEdited, setBodyManuallyEdited] = useState(false);
  const [isSending, setIsSending] = useState(false);

  // ── Attachments — the invoice PDF is always auto-attached; the PM can add more
  const [attachments, setAttachments] = useState<Attachment[]>([
    {
      id: 'invoice-pdf',
      name: `${invoiceNumber}.pdf`,
      size: '~245 KB',  // TODO (Engineering): Use real PDF size once server-side PDF generation is wired up
      isAutoAttached: true,
      file: null,
    },
  ]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAttachFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    const newAttachments: Attachment[] = files.map((f) => ({
      id: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`,
      name: f.name,
      size: formatFileSize(f.size),
      isAutoAttached: false,
      file: f,
    }));
    setAttachments((prev) => [...prev, ...newAttachments]);
    // Reset input so the same file can be re-attached if removed
    e.target.value = '';
  };

  const handleRemoveAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  };

  // Token data for merge field resolution — keys match {dot.notation} used in templates.
  // Memoised so the template-resolution effect below re-runs only when an
  // actual token value changes (e.g. clientEmail arrives asynchronously).
  const tokenData: Record<string, string> = useMemo(() => ({
    '{invoice.number}':          invoiceNumber,
    '{invoice.date}':            invoiceDate ?? new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
    '{invoice.dueDate}':         dueDate ?? '',
    '{invoice.total}':           invoiceTotal ?? '',
    '{invoice.amountDue}':       invoiceTotal ?? '',
    '{client.name}':             clientName ?? '',
    '{client.contactName}':      contactName ?? '',
    '{client.contactFirstName}': contactFirstName ?? '',
    '{client.email}':            clientEmail ?? '',
    '{client.phone}':            clientPhone ?? '',
    '{client.accountNumber}':    clientAccountNumber ?? '',
    '{client.paymentTermsDays}': clientPaymentTermsDays ?? '',
    '{project.name}':            projectName ?? '',
    '{project.code}':            projectCode ?? '',
    '{company.name}':            companyName ?? '',
    '{company.senderName}':      senderName ?? '',
    '{company.senderEmail}':     senderEmail ?? '',
  }), [
    invoiceNumber, invoiceDate, dueDate, invoiceTotal,
    clientName, contactName, contactFirstName, clientEmail, clientPhone,
    clientAccountNumber, clientPaymentTermsDays,
    projectName, projectCode, companyName, senderName, senderEmail,
  ]);

  useEffect(() => {
    const tpl = emailTemplates.find((t) => t.id === selectedTemplateId);
    if (!tpl) return;

    // While a saved draft is in force, never overwrite the restored
    // To/CC/Subject/Body with template defaults. This latch stays set across
    // asynchronous template (re)loads and only clears when the user explicitly
    // picks a different template (handleTemplateChange). Only fields the draft
    // did not carry are back-filled from the template.
    if (draftLatchRef.current) {
      if (!initialEmailDraft?.to) {
        const resolvedTo = tpl.to?.map((a) => resolveTokens(a, tokenData)).filter(Boolean) ?? [];
        setTo(resolvedTo.length > 0 ? resolvedTo : (clientEmail ? [clientEmail] : []));
      }
      if (!initialEmailDraft?.bcc) {
        setBcc(resolveAddresses(tpl.bcc, tokenData));
      }
      setPreviewHtml(resolveHtmlForSendPreview(tpl.bodyHtml, tokenData));
      if (initialEmailDraft?.body) setBodyManuallyEdited(true);
      return;
    }

    const resolvedTo = tpl.to?.map((a) => resolveTokens(a, tokenData)).filter(Boolean) ?? [];
    setTo(resolvedTo.length > 0 ? resolvedTo : (clientEmail ? [clientEmail] : []));
    setCc(resolveAddresses(tpl.cc, tokenData));
    setBcc(resolveAddresses(tpl.bcc, tokenData));
    setSubject(resolveTokens(tpl.subject, tokenData));
    // Derive body from the rich bodyHtml — strip chip spans, resolve tokens, convert to plain text
    setBody(resolveHtmlToPlainText(tpl.bodyHtml, tokenData));
    setBodyManuallyEdited(false);
    // Also store resolved HTML for the preview pane
    setPreviewHtml(resolveHtmlForSendPreview(tpl.bodyHtml, tokenData));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplateId, emailTemplates, tokenData, clientEmail]);

  /** User explicitly picked a template — release the draft latch so the newly
   *  selected template's defaults fully replace the current fields. */
  const handleTemplateChange = useCallback((id: string) => {
    draftLatchRef.current = false;
    setSelectedTemplateId(id);
  }, []);

  const CALLBACK_EVENT = 'projects:sendit-done';
  const mfePollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up MFE poll on unmount
  useEffect(() => {
    return () => {
      if (mfePollRef.current) clearInterval(mfePollRef.current);
    };
  }, []);

  const handleSendNow = useCallback(async () => {
    setIsSending(true);

    // Create the sales transaction and generate/store the PDF before opening the emailer
    let pdfForEmail = resolvedPdfBase64;
    let effectiveInvoiceNumber = invoiceNumber;
    if (onBeforeSend) {
      try {
        const result = await onBeforeSend();
        if (!result.success) {
          setIsSending(false);
          return;
        }
        if (result.pdfBase64) {
          pdfForEmail = result.pdfBase64;
          setResolvedPdfBase64(result.pdfBase64);
        }
        if (result.invoiceNumber) {
          effectiveInvoiceNumber = result.invoiceNumber;
        }
      } catch {
        toast.error('Failed to create invoice. Please try again.');
        setIsSending(false);
        return;
      }
    }

    const emailAttachments: { type: 'base64'; filename: string; mimeType: string; data: string }[] = [];
    if (pdfForEmail) {
      emailAttachments.push({
        type: 'base64',
        filename: `${effectiveInvoiceNumber}.pdf`,
        mimeType: 'application/pdf',
        data: pdfForEmail,
      });
    }

    // Convert manually added file attachments to base64 for the Evo Emailer
    const manualFiles = attachments.filter((a) => !a.isAutoAttached && a.file);
    for (const att of manualFiles) {
      try {
        const b64 = await blobToBase64(att.file!);
        emailAttachments.push({
          type: 'base64',
          filename: att.file!.name,
          mimeType: att.file!.type || 'application/octet-stream',
          data: b64,
        });
      } catch {
        console.warn(`Failed to encode attachment "${att.name}" — skipping.`);
      }
    }

    // Re-resolve email subject/body with the real invoice number if it changed
    let emailSubject = subject;
    let emailBody = bodyManuallyEdited ? body : (previewHtml || body);
    if (effectiveInvoiceNumber !== invoiceNumber) {
      emailSubject = subject.replaceAll(invoiceNumber, effectiveInvoiceNumber);
      emailBody = emailBody.replaceAll(invoiceNumber, effectiveInvoiceNumber);
    }

    const singleEmailPayload: SendItEmailPayload = {
      to,
      cc,
      ...(bcc.length > 0 ? { bcc } : {}),
      subject: emailSubject,
      body: emailBody,
      attachments: emailAttachments,
    };

    let callbackReceived = false;
    let sentSuccessfully = false;
    let mfeInstanceId: string | undefined;
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;

      if (!isSendItCallbackDetail(detail)) {
        console.warn('[SendIt MFE] Unexpected callback detail shape:', detail);
        logMfeFailure({
          mfeName: SENDIT_MFE_NAME,
          errorMessage: `Unexpected callback detail shape: ${JSON.stringify(detail)}`,
          errorType: 'unknown',
          component: 'SendInvoiceStep',
          timestamp: new Date().toISOString(),
          invoiceNumber: effectiveInvoiceNumber,
        });
      }

      const typedDetail = detail as SendItCallbackDetail;

      // MFE Cancel / Close / Dismiss button — clean up and notify parent.
      // After a successful send the MFE shows a success screen whose
      // "Close" button may fire with a different action value (e.g.
      // 'dismiss') or no action at all — so once sentSuccessfully is true
      // we treat any subsequent callback as the user dismissing the MFE.
      const isCloseAction =
        typedDetail?.action === 'close' ||
        typedDetail?.action === 'dismiss' ||
        typedDetail?.action === 'cancel' ||
        sentSuccessfully;

      if (isCloseAction) {
        window.removeEventListener(CALLBACK_EVENT, handler);
        callbackReceived = true;
        if (mfePollRef.current) {
          clearInterval(mfePollRef.current);
          mfePollRef.current = null;
        }
        if (mfeInstanceId) {
          closeMfe(mfeInstanceId).catch(() => {});
        }
        onMfeOpenChange?.(false);
        if (sentSuccessfully) {
          onSent?.();
        }
        onEmailerClosed?.();
        return;
      }

      // MFE success — close the MFE immediately so the user does not
      // see the intermediate "Email sent successfully" screen.
      sentSuccessfully = true;
      callbackReceived = true;
      window.removeEventListener(CALLBACK_EVENT, handler);
      if (mfePollRef.current) {
        clearInterval(mfePollRef.current);
        mfePollRef.current = null;
      }
      if (mfeInstanceId) {
        closeMfe(mfeInstanceId).catch(() => {});
      }
      onMfeOpenChange?.(false);
      toast.success(`Invoice ${effectiveInvoiceNumber} sent successfully.`);
      onSent?.();
      onEmailerClosed?.();
    };
    window.addEventListener(CALLBACK_EVENT, handler);

    onMfeOpenChange?.(true);

    try {
      const mfeOptions: SendItMfeOptions = {
        instanceId: instanceId ?? undefined,
        parameters: {
          mode: 'single',
          email: JSON.stringify(singleEmailPayload),
          callbackEventName: CALLBACK_EVENT,
          autoCloseOnSuccess: 'true',
        },
        type: 'modal',
      };
      const result = await openMfe(SENDIT_MFE_NAME, mfeOptions);
      mfeInstanceId = result.instanceId;
    } catch (err: unknown) {
      const isCloneError =
        err instanceof Error && err.message.includes('could not be cloned');
      if (!isCloneError) {
        window.removeEventListener(CALLBACK_EVENT, handler);
        toast.error('Failed to open SendIt. Please try again.');
        logMfeFailure({
          mfeName: SENDIT_MFE_NAME,
          errorMessage: err instanceof Error ? err.message : String(err),
          errorType: 'open_failed',
          component: 'SendInvoiceStep',
          timestamp: new Date().toISOString(),
          invoiceNumber: effectiveInvoiceNumber,
          instanceId: instanceId ?? undefined,
        });
        setIsSending(false);
        onMfeOpenChange?.(false);
        return;
      }
      // Clone error — MFE opened but the return value could not be
      // serialised.  Recover the instance ID from the platform registry
      // so the dismissal poller can still track it.
      try {
        const instances = window.AccessEvo?.mfes?.instances;
        if (instances) {
          const keys = Object.keys(instances);
          if (keys.length > 0) mfeInstanceId = keys[keys.length - 1];
        }
      } catch { /* registry unavailable — polling will use fallback */ }
    }

    // Poll for MFE dismissal (Cancel, X, or Close button).
    // Runs regardless of whether openMfe threw a clone error so we
    // always detect when the emailer is closed.
    if (mfePollRef.current) clearInterval(mfePollRef.current);
    mfePollRef.current = setInterval(() => {
      const instances = window.AccessEvo?.mfes?.instances;

      if (mfeInstanceId) {
        const instanceGone = !instances || !(mfeInstanceId! in instances);
        const instance = instances?.[mfeInstanceId!];
        const containerDetached =
          instance?.container && !document.body.contains(instance.container);

        if (instanceGone || containerDetached) {
          if (mfePollRef.current) clearInterval(mfePollRef.current);
          mfePollRef.current = null;
          if (!callbackReceived) {
            window.removeEventListener(CALLBACK_EVENT, handler);
          }
          onMfeOpenChange?.(false);
          if (sentSuccessfully) {
            onSent?.();
          }
          onEmailerClosed?.();
        }
      } else {
        // No instance ID (clone error + registry lookup failed).
        // Fall back to checking whether every MFE instance has been removed.
        const noInstancesLeft = !instances || Object.keys(instances).length === 0;
        if (noInstancesLeft) {
          if (mfePollRef.current) clearInterval(mfePollRef.current);
          mfePollRef.current = null;
          if (!callbackReceived) {
            window.removeEventListener(CALLBACK_EVENT, handler);
          }
          onMfeOpenChange?.(false);
          if (sentSuccessfully) {
            onSent?.();
          }
          onEmailerClosed?.();
        }
      }
    }, 300);

    setIsSending(false);
  }, [to, cc, bcc, subject, body, previewHtml, bodyManuallyEdited, invoiceNumber, instanceId, onSent, resolvedPdfBase64, onBeforeSend, onEmailerClosed, onMfeOpenChange, attachments]);

  return (
    <div className="space-y-6">
      {/* Template selector */}
      <div className="flex items-center gap-4">
        <div className="flex-1 space-y-1.5">
          <Label className="text-xs text-muted-foreground uppercase tracking-wide">
            Email Template
          </Label>
          <Select value={selectedTemplateId} onValueChange={handleTemplateChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select a template…" />
            </SelectTrigger>
            <SelectContent>
              {emailTemplates.map((tpl) => (
                <SelectItem key={tpl.id} value={tpl.id}>
                  <div className="flex items-center gap-2">
                    <span>{tpl.name}</span>
                    {tpl.isDefault && (
                      <Badge variant="secondary" className="text-xs py-0">Default</Badge>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Separator />

      {/* To / CC / BCC */}
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="send-to">To</Label>
          <EmailAddressInput
            id="send-to"
            value={to}
            onChange={setTo}
            placeholder="client@example.com"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="send-cc">
              CC <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <EmailAddressInput
              id="send-cc"
              value={cc}
              onChange={setCc}
              placeholder="cc@example.com"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="send-bcc">
              BCC <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <EmailAddressInput
              id="send-bcc"
              value={bcc}
              onChange={setBcc}
              placeholder="bcc@example.com"
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Press <kbd className="bg-muted border rounded px-1 py-0.5 text-[11px]">Enter</kbd> or <kbd className="bg-muted border rounded px-1 py-0.5 text-[11px]">,</kbd> after each address to add it.
        </p>
      </div>

      {/* Subject */}
      <div className="space-y-1.5">
        <Label htmlFor="send-subject">Subject</Label>
        <Input
          id="send-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>

      {/* Body — Preview / Edit tabs */}
      <div className="space-y-1.5">
        <Tabs defaultValue="preview">
          <div className="flex items-center justify-between mb-2">
            <Label>Message</Label>
            <TabsList className="h-7 px-1">
              <TabsTrigger value="preview" className="h-5 text-xs px-2 gap-1">
                <Eye className="h-3 w-3" />
                Preview
              </TabsTrigger>
              <TabsTrigger value="edit" className="h-5 text-xs px-2 gap-1">
                <Pencil className="h-3 w-3" />
                Edit
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Preview tab — rendered HTML with tokens resolved to real values */}
          <TabsContent value="preview" className="mt-0">
            <div
              className="
                min-h-[220px] rounded-md border bg-white px-4 py-3 text-sm leading-relaxed
                [&_p]:mb-3 [&_p:last-child]:mb-0
                [&_.resolved-token]:font-medium [&_.resolved-token]:text-foreground
                [&_.unresolved-token]:font-medium [&_.unresolved-token]:text-warning-600
                [&_strong]:font-semibold
              "
              dangerouslySetInnerHTML={{ __html: (bodyManuallyEdited ? plainTextToPreviewHtml(body) : previewHtml) || '<p class="text-muted-foreground italic">No template selected.</p>' }}
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              This is a preview of the email that will be sent. Switch to <strong>Edit</strong> to make changes.
            </p>
          </TabsContent>

          {/* Edit tab — plain-text textarea for manual overrides */}
          <TabsContent value="edit" className="mt-0">
            <Textarea
              id="send-body"
              value={body}
              onChange={(e) => { setBody(e.target.value); setBodyManuallyEdited(true); }}
              className="min-h-[220px] text-sm resize-none"
            />
            <p className="text-xs text-muted-foreground mt-1.5">
              Editing here overrides the template. Changes are reflected in the email that gets sent.
            </p>
          </TabsContent>
        </Tabs>
      </div>

      {/* Attachments */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="flex items-center gap-1.5">
            <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
            Attachments
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 text-xs gap-1.5"
            onClick={() => fileInputRef.current?.click()}
          >
            <Plus className="h-3.5 w-3.5" />
            Add File
          </Button>
          {/* Hidden file input — multiple allowed */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleAttachFiles}
          />
        </div>

        <div className="rounded-md border divide-y">
          {attachments.map((att) => (
            <div
              key={att.id}
              className="flex items-center gap-3 px-3 py-2.5 group"
            >
              {/* File icon */}
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />

              {/* Name + size */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{att.name}</p>
                <p className="text-xs text-muted-foreground">{att.size}</p>
              </div>

              {/* Auto-attached badge */}
              {att.isAutoAttached && (
                <Badge variant="secondary" className="text-[10px] py-0 h-4 shrink-0">
                  Invoice PDF
                </Badge>
              )}

              {/* Remove button — hidden for auto-attached; visible on hover for manual */}
              {!att.isAutoAttached && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                  onClick={() => handleRemoveAttachment(att.id)}
                  title="Remove attachment"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
              {/* Spacer so the row height is consistent when there's no remove button */}
              {att.isAutoAttached && <div className="w-6 shrink-0" />}
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          The invoice PDF is always included. You can attach additional supporting documents (e.g. timesheets, purchase orders).
        </p>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-2">
        <div className="flex gap-2">
          {onBack && (
            <Button variant="outline" onClick={onBack} disabled={isSending}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          )}
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          )}
        </div>
        <div className={cn('flex gap-2', isSending && 'cursor-wait')}>
          {onSaveAsDraft && (
            <Button
              variant="outline"
              onClick={() => {
                toast.success(`Invoice ${invoiceNumber} saved as draft.`);
                onSaveAsDraft({ to, cc, bcc, subject, body, templateId: selectedTemplateId });
              }}
              className="text-info-600 hover:text-info-700 hover:bg-info-50"
              disabled={isSending}
            >
              <Save className="h-4 w-4 mr-2" />
              Save as Draft
            </Button>
          )}
          <Button
            onClick={handleSendNow}
            disabled={to.length === 0 || !subject.trim() || isSending}
            size="sm"
            aria-busy={isSending}
            className={cn(isSending && 'cursor-wait')}
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {isSending ? 'Creating invoice…' : 'Send Now'}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Public export — embedded step or standalone dialog
// ─────────────────────────────────────────────────────────────────────────────

export function SendInvoiceStep(props: SendInvoiceStepProps) {
  const [mfeOpen, setMfeOpen] = useState(false);

  if (props.asDialog) {
    return (
      <Dialog open={props.open} onOpenChange={props.onOpenChange} modal={!mfeOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Send Invoice — {props.invoiceNumber}
            </DialogTitle>
            <DialogDescription>
              Review and edit the email before sending to the client.
            </DialogDescription>
          </DialogHeader>
          <SendForm {...props} onCancel={() => props.onOpenChange?.(false)} onMfeOpenChange={setMfeOpen} />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <div className="container mx-auto px-6 py-6 max-w-3xl">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Send Invoice</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Review and edit the email before sending {props.invoiceNumber} to the client.
        </p>
      </div>
      <SendForm {...props} />
    </div>
  );
}
